import { confirm, toast } from "@devhop/ui";
import { config } from "@/shared/lib/config";

export const CF_AUTH_EXPIRED_EVENT = "cf:auth-expired";

let authBoundaryState: "active" | "checking" | "expired" = "active";
let authExpiredConfirmPromise: Promise<boolean> | null = null;

export type CfSessionStatus = "active" | "expired" | "unavailable";

export function shouldBlockCfApiRequests(): boolean {
  return authBoundaryState !== "active";
}

export function resetCfAuthBoundary(): void {
  authBoundaryState = "active";
}

export function markCfAuthExpired(): void {
  if (authBoundaryState === "expired") return;
  authBoundaryState = "expired";
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(CF_AUTH_EXPIRED_EVENT));
  }
}

export async function getCfSessionStatus(
  request: typeof fetch = fetch,
): Promise<CfSessionStatus> {
  try {
    const response = await request(`${config.authUrl}/get-session`, {
      credentials: "include",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (response.status === 401 || response.status === 403) return "expired";
    if (!response.ok) return "unavailable";
    const body = (await response.json().catch(() => undefined)) as
      | { user?: unknown }
      | null
      | undefined;
    if (body === undefined) return "unavailable";
    return body?.user ? "active" : "expired";
  } catch {
    return "unavailable";
  }
}

export async function showCfAuthExpiredDialog(input?: {
  message?: string;
  checkSession?: boolean;
}): Promise<boolean> {
  if (
    typeof window === "undefined" ||
    window.location.pathname.startsWith("/auth/")
  ) {
    return false;
  }

  authExpiredConfirmPromise ??= (async () => {
    if (input?.checkSession !== false) {
      authBoundaryState = "checking";
      const status = await getCfSessionStatus();
      if (status !== "expired") {
        resetCfAuthBoundary();
        if (status === "active") {
          toast.error("ບໍ່ໄດ້ຮັບອະນຸຍາດ", {
            description: input?.message,
          });
        }
        return false;
      }
    }

    markCfAuthExpired();
    const confirmed = await confirm({
      title: "ເຊດຊັນໝົດອາຍຸ",
      description: "ກະລຸນາເຂົ້າລະບົບໃໝ່ເພື່ອດໍາເນີນການຕໍ່.",
      cancelText: "ປິດ",
      actionText: "ເຂົ້າລະບົບ",
      ActionProps: { variant: "default" },
    });
    window.location.replace("/auth/login");
    return confirmed;
  })().finally(() => {
    authExpiredConfirmPromise = null;
  });

  return authExpiredConfirmPromise;
}
